'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import { Link } from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';

import { TextEditorToolbar } from './text-editor-toolbar';

/**
 * A thin wrapper around tiptap.
 *
 * How to get the updated content:
 * ```ts
 * <TextEditor
 *   onUpdate={({ editor }) => {
 *     console.log(editor.getHTML());
 *   }}
 * />
 * ```
 */
export function TextEditor({
  content,
  editable = true,
  hideToolbar,
  onUpdate = () => {
    // noop
  },
  placeholder,
}) {
  const extensions = [
    StarterKit,
    Placeholder.configure({ emptyEditorClass: 'is-editor-empty', placeholder }),
    Link.configure({ openOnClick: false, autolink: true }),
    Table.configure({ resizable: true }),
    TableRow,
    TableHeader,
    TableCell,
  ];

  const editor = useEditor({ extensions, content, editable, onUpdate });

  React.useEffect(() => {
    if (!editor) {
      return;
    }
    if (content === undefined) {
      return;
    }
    const next = content || '';
    if (editor.getHTML() !== next) {
      editor.commands.setContent(next, false);
    }
  }, [content, editor]);

  return (
    <Box
      className="tiptap-root"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(editable && {
          border: '1px solid var(--mui-palette-divider)',
          borderRadius: 1,
          boxShadow: 'var(--mui-shadows-1)',
        }),
        '& .tiptap-container': { display: 'flex', flex: '1 1 auto', flexDirection: 'column', minHeight: 0 },
        '& .tiptap': {
          color: 'var(--mui-palette-text-primary)',
          flex: '1 1 auto',
          overflow: 'auto',
          p: '8px 16px',
          '&:focus-visible': { outline: 'none' },
          '&.resize-cursor': { cursor: 'ew-resize', '& table': { cursor: 'col-resize' } },
          '& .is-editor-empty:before': {
            color: 'var(--mui-palette-text-secondary)',
            content: 'attr(data-placeholder)',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
          },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
          },
          '& th, & td': {
            border: '1px solid var(--mui-palette-divider)',
            padding: '6px 8px',
            textAlign: 'left',
            verticalAlign: 'top',
          },
          '& th': {
            backgroundColor: 'var(--mui-palette-grey-100)',
            fontWeight: 600,
          },
        },
      }}
    >
      {!hideToolbar ? <TextEditorToolbar editor={editor} /> : <div />}
      <EditorContent className="tiptap-container" editor={editor} />
    </Box>
  );
}
